import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  getStationeryItemTag,
  getStationeryTag,
  getStatusTag,
} from "../../components/filter/FilterEnum";
import ModalAddStationeryItems from "../../components/modal/hrm/ModalAddStationeryItems";
import {
  StationeryItemStatus,
  StationeryStatus,
  Status,
  StepStatus,
} from "../../enum/common";
import {
  IStationeryRequest,
  IUpdateStationeryRequestItem,
} from "../../models/Hrm";
import {
  useDeleteStationeryRequestItem,
  useUpdateNoteStationeryRequest,
  useUpdateStationeryRequestItems,
} from "../../query/HrmQuery";
import { notify } from "../../utils/toast";
interface Props {
  data: IStationeryRequest;
}
const { Title } = Typography;
const PersonalStationeryItems = ({ data }: Props) => {
  const { Text } = Typography;
  const [note, setNote] = useState<string>("");
  const [editNote, setEditNote] = useState<boolean>(false);
  const navigate = useNavigate();
  const mutationDelete = useDeleteStationeryRequestItem();
  const mutationUpdate = useUpdateStationeryRequestItems();
  const mutationUpdateNote = useUpdateNoteStationeryRequest();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editingKey, setEditingKey] = useState<string | number>("");
  const [form, setForm] = useState<IUpdateStationeryRequestItem>();

  const isReadonly =
    data?.period.periodStatus === StationeryStatus.CLOSED ||
    data?.period.approvalStatus === Status.REJECTED;

  const handleDelete = async () => {
    try {
      if (selectedRowKeys.length > 0) {
        await mutationDelete.mutateAsync(selectedRowKeys as number[]);
        setSelectedRowKeys([]);
        notify.success("Xóa thành công");
      }
    } catch (e) {
      notify.error("Lỗi khi xóa vật phẩm");
    }
  };

  const handleEdit = async () => {
    try {
      if (form) {
        await mutationUpdate.mutateAsync([form]);
        setEditingKey("");
        notify.success("Sửa đổi thành công");
      }
    } catch (e) {
      notify.error("Lỗi");
    }
  };
  const handleInputChange = (
    value: string | number,
    key: "name" | "quantity" | "note",
  ) => {
    setForm((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [key]: value,
      };
    });
  };

  const tableData = data?.items || [];

  const handleSaveNote = async () => {
    try {
      await mutationUpdateNote.mutateAsync({
        id: data.id,
        note,
      });
      setEditNote(false);
      notify.success("Cập nhật ghi chú thành công");
    } catch (e) {
      notify.error("Lỗi khi cập nhật ghi chú");
    }
  };
  const totalQuantity =
    data?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  const totalAmount =
    data?.items?.reduce(
      (sum, item) =>
        sum + (Number(item.product?.price) || 0) * item.quantity * 1000,
      0,
    ) || 0;
  return (
    <Flex vertical className="px-3 gap-4 w-full mt-3 overflow-hidden">
      <Flex align="center" justify="space-between" wrap="wrap" gap="small">
        <Button
          type="default"
          size="large"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
        >
          Quay lại
        </Button>
        {isReadonly && (
          <Tag color="red" className="p-1 m-0 text-sm">
            {data?.period.periodStatus === StationeryStatus.CLOSED
              ? "Kỳ mua văn phòng phẩm đã đóng"
              : "Yêu cầu đã bị từ chối"}
          </Tag>
        )}
      </Flex>

      <Card bordered={false} className="shadow-sm">
        <Flex justify="space-between" align="start" wrap="wrap" gap="middle">
          <Flex vertical gap={4}>
            <Text type="secondary">KỲ ĐĂNG KÝ VĂN PHÒNG PHẨM</Text>

            <Title level={2} style={{ margin: 0 }}>
              {data?.period?.name}
            </Title>

            <Space>
              <Tag
                color={
                  getStatusTag(
                    data?.period?.approvalStatus as any as StepStatus,
                  ).color
                }
              >
                {
                  getStatusTag(
                    data?.period?.approvalStatus as any as StepStatus,
                  ).label
                }
              </Tag>
              <Tag color={getStationeryTag(data?.period?.periodStatus).color}>
                {getStationeryTag(data?.period?.periodStatus).label}
              </Tag>
            </Space>
          </Flex>
        </Flex>

        <Divider />

        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Card size="small" className="bg-blue-50/50">
              <Statistic title="Tổng vật phẩm" value={tableData.length} />
            </Card>
          </Col>

          <Col xs={24} md={8}>
            <Card size="small" className="bg-green-50/50">
              <Statistic title="Tổng số lượng" value={totalQuantity} />
            </Card>
          </Col>

          <Col xs={24} md={8}>
            <Card size="small" className="bg-purple-50/50">
              <Statistic
                title="Tổng tiền ước tính"
                value={totalAmount}
                formatter={(v) => Number(v).toLocaleString("vi-VN")}
                suffix="VNĐ"
              />
            </Card>
          </Col>
        </Row>

        <Divider />

        <Card size="small" title="📝 Ghi chú yêu cầu" className="bg-gray-50/50">
          {!editNote ? (
            <Flex justify="space-between" align="center">
              <Text>{data?.note || "Chưa có ghi chú"}</Text>

              <Button
                type="text"
                icon={<EditOutlined className="text-blue-500" />}
                disabled={isReadonly}
                onClick={() => {
                  setNote(data?.note || "");
                  setEditNote(true);
                }}
              />
            </Flex>
          ) : (
            <Space.Compact
              style={{
                width: "100%",
              }}
            >
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onPressEnter={handleSaveNote}
                autoFocus
              />

              <Button
                icon={<CloseOutlined />}
                onClick={() => setEditNote(false)}
              />

              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={handleSaveNote}
              />
            </Space.Compact>
          )}
        </Card>
      </Card>

      <Card
        title="Danh sách vật phẩm"
        className="shadow-sm"
        extra={
          <Space>
            {selectedRowKeys.length > 0 && !isReadonly && (
              <Popconfirm
                title="Xóa vật phẩm?"
                description={`Bạn có chắc chắn muốn xóa ${selectedRowKeys.length} vật phẩm đã chọn?`}
                onConfirm={handleDelete}
                okText="Đồng ý"
                cancelText="Hủy"
              >
                <Button danger icon={<DeleteOutlined />}>
                  Xóa đã chọn ({selectedRowKeys.length})
                </Button>
              </Popconfirm>
            )}
            {!isReadonly && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setIsModalOpen(true)}
              >
                Thêm vật phẩm
              </Button>
            )}
          </Space>
        }
      >
        <Table
          rowKey="id"
          scroll={{ y: 400, x: 1000 }}
          pagination={false}
          dataSource={tableData}
          rowSelection={{
            columnWidth: 60,
            selectedRowKeys,
            getCheckboxProps: (record) => ({
              disabled: isReadonly,
            }),
            onChange: (keys) => {
              setSelectedRowKeys(keys);
            },
          }}
          columns={[
            {
              title: "Mã",
              dataIndex: "id",
              align: "center",
              sorter: (a, b) => a.id - b.id,
              defaultSortOrder: "ascend",
              render: (val) => <Tag color="blue">#{val}</Tag>,
            },
            {
              title: "Tên vật phẩm",
              dataIndex: "product",
              render: (value, record) => (
                <Typography.Text strong>{value?.name}</Typography.Text>
              ),
              filterDropdown({ setSelectedKeys, selectedKeys, confirm }) {
                return (
                  <Input
                    value={selectedKeys[0]}
                    placeholder="Tìm tên vật phẩm"
                    onChange={(e) => {
                      setSelectedKeys(e.target.value ? [e.target.value] : []);
                      confirm({ closeDropdown: false });
                    }}
                  />
                );
              },
              onFilter: (value, record) =>
                (record.product?.name ?? "")
                  .toLowerCase()
                  .includes(String(value).toLowerCase()),
            },
            {
              title: "Số lượng",
              dataIndex: "quantity",
              align: "center",
              render(value, record) {
                const editable = record.id === editingKey;
                return editable ? (
                  <InputNumber
                    size="large"
                    min={1}
                    value={form?.quantity}
                    onChange={(val) => handleInputChange(val ?? 0, "quantity")}
                  />
                ) : (
                  <Typography.Text strong>{value}</Typography.Text>
                );
              },
              filterDropdown({ setSelectedKeys, selectedKeys, confirm }) {
                return (
                  <InputNumber
                    placeholder="Tìm Số lượng"
                    min={1}
                    className="w-full"
                    onChange={(e) => {
                      setSelectedKeys(e ? [e] : []);
                      confirm({ closeDropdown: false });
                    }}
                  />
                );
              },
              onFilter: (value, record) => {
                return record.quantity === value;
              },
            },
            {
              title: "Trạng thái",
              dataIndex: "statusRequest",
              align: "center",
              filterDropdown({ setSelectedKeys, selectedKeys, confirm }) {
                return (
                  <Select
                    value={selectedKeys[0]}
                    placeholder="Chọn trạng thái"
                    className="w-full"
                    onChange={(value) => {
                      const v = value ? String(value) : undefined;
                      setSelectedKeys(v ? [v] : []);
                      confirm?.();
                    }}
                    options={[
                      { value: StationeryItemStatus.IS_BUYED, label: "Đã mua" },
                      {
                        value: StationeryItemStatus.IS_BUYING,
                        label: "Đang mua",
                      },
                      {
                        value: StationeryItemStatus.IS_NOT_BUYED,
                        label: "Không mua",
                      },
                      {
                        value: StationeryItemStatus.IS_NOT_BUY_YET,
                        label: "Chưa mua",
                      },
                      {
                        value: StationeryItemStatus.IS_RETURNED,
                        label: "Đã trả lại",
                      },
                    ]}
                    allowClear
                  />
                );
              },
              render(value, record) {
                return (
                  <Tag
                    color={getStationeryItemTag(value).color}
                    className="px-2 py-1  m-0 text-sm"
                  >
                    {getStationeryItemTag(value).label}
                  </Tag>
                );
              },
              onFilter: (value, record) => {
                return record.statusRequest === value;
              },
            },
            {
              title: "Đơn giá",
              align: "center",
              ellipsis: true,
              render(_, record) {
                return (
                  ((Number(record.product?.price) || 0) * 1000).toLocaleString(
                    "vi-VN",
                  ) + " VND"
                );
              },
            },
            {
              title: "Thành tiền",
              align: "center",
              ellipsis: true,
              render(value, record) {
                return record.product?.price
                  ? (
                      (Number(record.product.price) || 0) *
                      record.quantity *
                      1000
                    ).toLocaleString("vi-VN") + " VND"
                  : "0.000 VND";
              },
            },
            {
              title: "Ghi chú",
              dataIndex: "note",
              align: "center",
              width: 150,
              className: "cursor-pointer",
              ellipsis: true,
              render(value, record) {
                const editable = record.id === editingKey;
                return editable ? (
                  <Input
                    value={form?.note}
                    onChange={(e) => handleInputChange(e.target.value, "note")}
                    size="large"
                  />
                ) : (
                  record.note
                );
              },
              filterIcon: (filtered: boolean) => (
                <SearchOutlined
                  style={{ color: filtered ? "#1890ff" : undefined }}
                />
              ),
              onFilter: (value, record) => {
                return record.note === value;
              },
            },
            {
              title: "Nhận xét",
              dataIndex: "remark",
              align: "center",
              ellipsis: true,
            },
            {
              title: "Thao tác",
              align: "center",
              render: (value, record) => {
                const editable = record.id === editingKey;
                return editable ? (
                  <Space size="middle">
                    <Button onClick={handleEdit} size="small" type="primary">
                      <CheckOutlined />
                    </Button>
                    <Popconfirm
                      title="Hủy bỏ thay đổi?"
                      description="Các thay đổi bạn vừa nhập sẽ không được lưu."
                      onConfirm={() => setEditingKey("")}
                      okText="Đồng ý"
                      cancelText="Quay lại"
                      okButtonProps={{ danger: true }}
                    >
                      <Button danger size="small">
                        <CloseOutlined />
                      </Button>
                    </Popconfirm>
                  </Space>
                ) : (
                  <Space>
                    <Button
                      type="text"
                      icon={<EditOutlined className="text-blue-500" />}
                      disabled={isReadonly}
                      onClick={() => {
                        setEditingKey(record.id);
                        setForm(
                          record as unknown as IUpdateStationeryRequestItem,
                        );
                      }}
                    >
                      Chỉnh sửa
                    </Button>
                  </Space>
                );
              },
            },
            Table.SELECTION_COLUMN,
          ]}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={2}>
                <Text strong>Tổng cộng</Text>
              </Table.Summary.Cell>

              <Table.Summary.Cell index={1}>
                <Text strong>{totalQuantity}</Text>
              </Table.Summary.Cell>

              <Table.Summary.Cell index={2} colSpan={6}>
                <Text strong>{totalAmount.toLocaleString("vi-VN")} VNĐ</Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      </Card>

      <ModalAddStationeryItems
        isOpen={isModalOpen}
        onClose={setIsModalOpen}
        reqId={data.id!}
      />
    </Flex>
  );
};

export default PersonalStationeryItems;
